import React, { useCallback, useEffect, useRef, useState } from "react";
import { MEDIA_TYPES, PLAYLIST_ITEM_TYPES } from "../lib/constants";
import { Newspaper, Volume2, VolumeX } from "lucide-react";
import { useMediaSound } from "../lib/useMediaSound";
import { subscribeDuck } from "../lib/mediaDuck";
import { createYouTubePlayer } from "../lib/ytPlayer";
import { bgStyle } from "../lib/bgStyle";

const toEmbedUrl = (url, withSound = false) => {
  if (!url) return "";
  const mute = withSound ? 0 : 1;
  if (url.includes("/embed/")) {
    const sep = url.includes("?") ? "&" : "?";
    const clean = url.replace(/([?&])mute=\d(&|$)/, (_, a, b) => (b ? a : "")).replace(/[?&]$/, "");
    return `${clean}${sep}mute=${mute}&autoplay=1&enablejsapi=1`;
  }
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (ytMatch) {
    const id = ytMatch[1];
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=${mute}&loop=1&playlist=${id}&controls=0&modestbranding=1&rel=0&enablejsapi=1`;
  }
  return url;
};

// Post a YouTube IFrame API command to control the embedded player.
// Requires enablejsapi=1 in the embed URL (already set in toEmbedUrl).
const ytCmd = (iframeEl, func, args = []) => {
  if (!iframeEl || !iframeEl.contentWindow) return;
  try {
    iframeEl.contentWindow.postMessage(
      JSON.stringify({ event: "command", func, args }),
      "*"
    );
  } catch {
    /* ignore cross-origin failures */
  }
};

/**
 * SDK-backed YouTube player. Uses the official IFrame Player API for reliable
 * volume/mute control (postMessage alone is racy because commands fire before
 * the player is ready).
 *
 * Props:
 *   url           — YouTube URL (any format)
 *   soundEnabled  — when true, player will unmute and obey volume; when false, stays muted
 *   volume        — 0..1 (mapped to 0..100 for the SDK)
 *   ducked        — externally controlled mute (for TTS announcement). When true, mutes regardless of soundEnabled.
 *   "data-testid" passthrough for tests.
 */
const YouTubePlayer = ({ url, soundEnabled, volume, ducked = false, ...rest }) => {
  const hostRef = useRef(null);
  const controllerRef = useRef(null);
  const [ytError, setYtError] = useState(null);

  // Create / recreate player when URL changes
  useEffect(() => {
    if (!hostRef.current || !url) return undefined;
    setYtError(null);
    // Mount a fresh child div for the SDK to swap with an iframe
    const child = document.createElement("div");
    child.style.width = "100%";
    child.style.height = "100%";
    hostRef.current.innerHTML = "";
    hostRef.current.appendChild(child);

    const controller = createYouTubePlayer(child, url, {
      startMuted: !soundEnabled,
      onError: (err) => setYtError(err),
    });
    controllerRef.current = controller;
    return () => {
      controller.destroy();
      controllerRef.current = null;
    };
    // Intentionally NOT depending on soundEnabled/volume here — those are pushed via separate effects
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // Apply mute/unmute + volume whenever these change
  useEffect(() => {
    const c = controllerRef.current;
    if (!c) return;
    const vol = Math.round(Math.max(0, Math.min(1, volume ?? 1)) * 100);
    c.setVolume(vol);
    if (ducked || !soundEnabled) c.mute();
    else c.unMute();
  }, [soundEnabled, volume, ducked]);

  return (
    <div ref={hostRef} className="w-full h-full relative" {...rest}>
      {ytError && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center bg-foreground text-background p-8"
          data-testid="media-youtube-error"
        >
          <div className="max-w-xl text-center">
            <div className="text-xs uppercase tracking-[0.3em] text-background/60 mb-4">
              Mídia indisponível · YouTube
            </div>
            <h3 className="font-heading text-3xl md:text-4xl font-black tracking-tighter leading-tight mb-4">
              {ytError.kind === "embed-blocked"
                ? "Esta transmissão não pode ser exibida aqui"
                : ytError.kind === "not-found"
                ? "Vídeo não encontrado"
                : ytError.kind === "invalid-url"
                ? "Endereço do YouTube inválido"
                : "Não foi possível carregar o vídeo"}
            </h3>
            <p className="text-sm md:text-base text-background/70 leading-relaxed">
              {ytError.kind === "embed-blocked"
                ? "O criador da transmissão bloqueou a exibição fora do YouTube. Comum em jogos de futebol e eventos com direitos esportivos. Troque para outra mídia ou use 'Slides' / 'MP4' no painel."
                : ytError.message}
            </p>
            <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-background/40 font-mono">
              Cód. YouTube: {ytError.code}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SlideShow = ({ slides, intervalSec }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!slides || slides.length <= 1) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, Math.max(2, intervalSec || 6) * 1000);
    return () => clearInterval(id);
  }, [slides, intervalSec]);

  if (!slides || slides.length === 0) {
    return <div className="text-foreground/50">Nenhuma imagem configurada</div>;
  }
  return (
    <div className="relative w-full h-full overflow-hidden">
      {slides.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt={`slide-${i}`}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ opacity: i === index ? 1 : 0 }}
          data-testid={`media-slide-${i}`}
        />
      ))}
    </div>
  );
};

const RSSFeed = ({ url }) => {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    const api = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(url)}`;
    fetch(api)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.status === "ok") setItems(data.items.slice(0, 6));
        else setErr("Não foi possível carregar o feed RSS");
      })
      .catch(() => !cancelled && setErr("Erro de rede ao carregar RSS"));
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <div className="w-full h-full overflow-y-auto p-8 md:p-12 bg-background" data-testid="rss-feed">
      <div className="flex items-center gap-3 mb-8">
        <Newspaper className="w-7 h-7" />
        <h3 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Últimas Notícias</h3>
      </div>
      {err && <div className="text-destructive">{err}</div>}
      <ul className="space-y-6">
        {items.map((it, i) => (
          <li key={i} className="border-b border-border pb-5">
            <div className="text-xs uppercase tracking-[0.2em] text-foreground/60 mb-2">
              {new Date(it.pubDate).toLocaleString()}
            </div>
            <div className="text-xl md:text-2xl font-semibold leading-snug">{it.title}</div>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ---- Mixed-format Playlist Player ----
const shuffleArray = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const PlaylistPlayer = ({ items, shuffle, soundEnabled, mediaVolume = 1 }) => {
  // Order is derived once on mount. Parent re-mounts this component (via `key`)
  // whenever items/shuffle change, which is the cleanest way to reset state.
  const [order, setOrder] = useState(() => (shuffle ? shuffleArray(items) : items));
  const [index, setIndex] = useState(0);
  const [ducked, setDucked] = useState(false);
  const timerRef = useRef(null);
  const videoRef = useRef(null);

  // Sync MP4 element muted state & volume with preferences
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !soundEnabled || ducked;
    v.volume = mediaVolume;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [soundEnabled, mediaVolume, index, ducked]);

  // Audio ducking during TTS announcement — MP4 controlled via .muted above;
  // YouTube controlled via the YouTubePlayer 'ducked' prop.
  useEffect(() => subscribeDuck((d) => setDucked(d)), []);

  const advance = useCallback(() => {
    setIndex((i) => {
      const len = order.length;
      if (len === 0) return 0;
      const nextIdx = (i + 1) % len;
      // Re-shuffle when wrapping back to 0 if shuffle is on
      if (shuffle && nextIdx === 0) setOrder(shuffleArray(items));
      return nextIdx;
    });
  }, [order.length, shuffle, items]);

  const current = order[index];

  // Schedule advancement based on item type + duration
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!current || order.length <= 1) return undefined;

    const duration = Math.max(2, Number(current.duration) || 10) * 1000;

    // For MP4 we wait for video natural end, but enforce a safety cutoff at duration*3
    if (current.type === PLAYLIST_ITEM_TYPES.MP4) {
      timerRef.current = setTimeout(advance, duration * 3);
    } else {
      timerRef.current = setTimeout(advance, duration);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [current, order.length, advance]);

  if (!items || items.length === 0) {
    return <div className="text-foreground/50">Adicione itens à playlist no painel admin</div>;
  }
  if (!current) return null;

  return (
    <div className="relative w-full h-full overflow-hidden" data-testid="media-playlist">
      <div className="absolute inset-0" key={`${current.id || index}-${current.type}-${current.url}`}>
        {current.type === PLAYLIST_ITEM_TYPES.IMAGE && (
          <img
            src={current.url}
            alt="playlist-item"
            className="w-full h-full object-cover"
            data-testid="playlist-image"
          />
        )}
        {current.type === PLAYLIST_ITEM_TYPES.YOUTUBE && (
          <YouTubePlayer
            key={`yt-${current.id || index}`}
            url={current.url}
            soundEnabled={soundEnabled}
            volume={mediaVolume}
            ducked={ducked}
            data-testid="playlist-youtube"
          />
        )}
        {current.type === PLAYLIST_ITEM_TYPES.MP4 && (
          <video
            ref={videoRef}
            src={current.url}
            className="w-full h-full object-cover"
            autoPlay
            muted={!soundEnabled}
            playsInline
            onEnded={advance}
            data-testid="playlist-mp4"
          />
        )}
        {current.type === PLAYLIST_ITEM_TYPES.RSS && (
          <RSSFeed url={current.url} />
        )}
      </div>

      {/* Progress indicator (dots) */}
      {order.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {order.map((_, i) => (
            <span
              key={i}
              className="block w-8 h-[3px] transition-colors"
              style={{ backgroundColor: i === index ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.35)" }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const MediaZone = ({ media, bgColor, bgOpacity = 1, bgImage = "", silentByDefault = false }) => {
  const { type } = media;
  const mediaVolume = typeof media.volume === "number" ? Math.max(0, Math.min(1, media.volume)) : 1;
  const { enabled: soundEnabled, decided: soundDecided, setSound, toggle: toggleSound } = useMediaSound();
  const mp4Ref = useRef(null);
  const [ducked, setDucked] = useState(false);

  // Keep MP4 video element in sync with sound preference, volume & ducking
  useEffect(() => {
    const v = mp4Ref.current;
    if (!v) return;
    v.muted = !soundEnabled || ducked;
    v.volume = mediaVolume;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }, [soundEnabled, type, media.mp4Url, mediaVolume, ducked]);

  // Subscribe to TTS ducking events (mute media during announcement)
  useEffect(() => subscribeDuck((d) => setDucked(d)), []);

  const hasAudioCapableMedia =
    (type === MEDIA_TYPES.YOUTUBE && media.youtubeUrl) ||
    (type === MEDIA_TYPES.MP4 && media.mp4Url) ||
    (type === MEDIA_TYPES.PLAYLIST &&
      (media.playlist || []).some(
        (it) => it.type === PLAYLIST_ITEM_TYPES.YOUTUBE || it.type === PLAYLIST_ITEM_TYPES.MP4
      ));

  // If silentByDefault is set in admin config (24/7 unattended mode), auto-decide as muted
  // so the prompt never blocks the screen after a power-cycle.
  useEffect(() => {
    if (silentByDefault && !soundDecided && hasAudioCapableMedia) {
      setSound(false);
    }
  }, [silentByDefault, soundDecided, hasAudioCapableMedia, setSound]);

  const showSoundPrompt = hasAudioCapableMedia && !soundDecided && !silentByDefault;

  return (
    <div
      className="relative w-full h-full flex items-center justify-center overflow-hidden"
      style={bgStyle(bgColor, bgOpacity, bgImage)}
      data-testid="media-zone"
    >
      {type === MEDIA_TYPES.YOUTUBE && media.youtubeUrl && (
        <YouTubePlayer
          url={media.youtubeUrl}
          soundEnabled={soundEnabled}
          volume={mediaVolume}
          ducked={ducked}
          data-testid="media-youtube"
        />
      )}
      {type === MEDIA_TYPES.MP4 && media.mp4Url && (
        <video
          ref={mp4Ref}
          src={media.mp4Url}
          className="w-full h-full object-cover"
          autoPlay
          muted={!soundEnabled}
          loop
          playsInline
          data-testid="media-mp4"
        />
      )}
      {type === MEDIA_TYPES.SLIDES && (
        <SlideShow slides={media.slides} intervalSec={media.slideInterval} />
      )}
      {type === MEDIA_TYPES.RSS && <RSSFeed url={media.rssUrl} />}
      {type === MEDIA_TYPES.PLAYLIST && (
        <PlaylistPlayer
          key={`pl-${(media.playlist || []).length}-${!!media.playlistShuffle}`}
          items={media.playlist || []}
          shuffle={!!media.playlistShuffle}
          soundEnabled={soundEnabled}
          mediaVolume={mediaVolume}
        />
      )}
      {type === MEDIA_TYPES.NONE && (
        <div className="text-foreground/40 font-heading text-3xl tracking-tight">Mídia desativada</div>
      )}

      {hasAudioCapableMedia && (
        <button
          type="button"
          onClick={toggleSound}
          className={`absolute bottom-4 right-4 z-20 flex items-center gap-2 px-3 py-2 backdrop-blur-md border transition-all duration-200 text-xs uppercase tracking-[0.2em] ${
            soundEnabled
              ? "bg-foreground/80 text-background border-foreground/80 hover:bg-foreground"
              : "bg-background/40 text-foreground border-foreground/30 hover:bg-background/70 animate-pulse"
          }`}
          aria-label={soundEnabled ? "Desativar som" : "Ativar som"}
          title={soundEnabled ? "Desativar som" : "Ativar som da mídia"}
          data-testid="media-sound-toggle"
        >
          {soundEnabled ? (
            <>
              <Volume2 className="w-4 h-4" /> Som ligado
            </>
          ) : (
            <>
              <VolumeX className="w-4 h-4" /> Ativar som
            </>
          )}
        </button>
      )}

      {showSoundPrompt && (
        <div
          className="absolute inset-0 z-30 flex items-center justify-center bg-foreground/70 backdrop-blur-sm p-6"
          data-testid="media-sound-prompt"
        >
          <div className="bg-background border-2 border-foreground shadow-2xl max-w-md w-full p-8 md:p-10">
            <div className="flex items-center gap-3 mb-4">
              <Volume2 className="w-6 h-6" />
              <span className="text-xs uppercase tracking-[0.3em] text-foreground/60">Configurar Áudio</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter leading-[0.95] mb-3">
              Ativar som da mídia?
            </h2>
            <p className="text-sm md:text-base text-foreground/70 leading-relaxed mb-8">
              Seu navegador exige permissão para tocar áudio automaticamente. Confirme abaixo
              para ouvir o som dos vídeos. A chamada de senhas (voz) continua funcionando
              normalmente em qualquer opção.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSound(false)}
                className="border-2 border-foreground text-foreground hover:bg-foreground hover:text-background transition-colors h-14 text-xs uppercase tracking-[0.25em] font-semibold"
                data-testid="media-sound-prompt-no"
              >
                <VolumeX className="w-4 h-4 inline mr-2" /> Sem som
              </button>
              <button
                type="button"
                onClick={() => setSound(true)}
                className="bg-foreground text-background hover:bg-foreground/90 transition-colors h-14 text-xs uppercase tracking-[0.25em] font-semibold"
                data-testid="media-sound-prompt-yes"
              >
                <Volume2 className="w-4 h-4 inline mr-2" /> Ativar som
              </button>
            </div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-foreground/50 mt-6 text-center">
              Você pode mudar a qualquer momento no botão do canto inferior direito
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
